/**
 * /team-boards — see and edit any team member's home widget board (Aug 26, 2026).
 *
 * Greg: "a board that allows me to log in to each team member's board and see
 * their set up and even edit it for them. a dropdown list is sufficient."
 *
 * Pick a person from the dropdown → see exactly which widgets their home page
 * renders, in order, and where that board came from (a board you saved, a
 * hand-set per-person default in registry.ts, or their role default). Edit it,
 * preview it, save it. Saving writes `user_widget_layout`; Reset deletes the
 * row so they fall straight back to the code default.
 *
 * Two rules this page will not let you break, because both mirror a server
 * gate and breaking them renders an error card instead of data:
 *   • the widget's role gate (the board / RPC behind it admits that role)
 *   • the per-person `excludeEmails` carve-out, which beats super-user status
 * Widgets that fail either are shown greyed with the reason, never checkable.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { LANDING_BY_ROLE } from "../components/home/homeNav";
import WidgetBoard from "../components/home/widgets/WidgetBoard";
import {
  WIDGETS, WIDGET_ORDER, DEFAULT_WIDGETS_BY_ROLE, WIDGET_OVERRIDES_BY_MEMBER,
  boardSource, defaultWidgetsFor, ineligibleReason, isWidgetEligible, resolveWidgets,
  type BoardSource, type WidgetKey,
} from "../components/home/widgets/registry";
import {
  fetchAllWidgetLayouts, resetWidgetLayout, saveWidgetLayout, type WidgetLayoutRow,
} from "../lib/widgetLayouts";

interface Member {
  id: string;
  full_name: string;
  email: string | null;
  role: string;
  secondary_role: string | null;
}

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  sales_director: "Sales Director",
  director_of_operations: "Director of Operations",
  roof_director: "Roof Director",
  roof_team_manager: "Roof Team Manager",
  asm: "ASM",
  team_lead: "Team Lead",
  restore_manager: "Restore Manager",
  roof_specialist: "Roof Specialist",
  drone_pilot: "Drone Pilot",
  report_specialist: "Report Specialist",
  finance: "Finance",
  office: "Office",
  qc: "QC",
  canvasser: "Canvasser",
  project_manager: "Project Manager",
};
const roleLabel = (r: string | null | undefined) =>
  (r && (ROLE_LABEL[r] ?? r.replace(/_/g, " "))) || "no role";

// Role display order for the dropdown's optgroups — leadership first, then the
// field, then support. Anything unlisted falls to the end alphabetically.
const ROLE_ORDER = [
  "admin", "sales_director", "director_of_operations", "roof_director",
  "roof_team_manager", "asm", "team_lead", "restore_manager",
  "roof_specialist", "drone_pilot", "report_specialist",
  "finance", "office", "qc", "project_manager", "canvasser",
];

const SOURCE_CHIP: Record<BoardSource, { label: string; cls: string; hint: string }> = {
  custom: {
    label: "Custom board",
    cls: "bg-orange-50 text-orange-700 border-orange-200",
    hint: "Saved here. Overrides their role default.",
  },
  person: {
    label: "Per-person default",
    cls: "bg-sky-50 text-sky-700 border-sky-200",
    hint: "Hand-set in registry.ts for this person. Saving here overrides it.",
  },
  role: {
    label: "Role default",
    cls: "bg-slate-100 text-slate-600 border-slate-200",
    hint: "Everyone in this role gets this board.",
  },
  none: {
    label: "No board",
    cls: "bg-slate-100 text-slate-500 border-slate-200",
    hint: "This role has no default board — their home shows the schedule only.",
  },
};

function sameKeys(a: WidgetKey[], b: WidgetKey[]) {
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

export default function TeamBoards() {
  const { member: viewer, role: viewerRole, isSuperUser } = useAuth();
  const canEdit =
    !!isSuperUser ||
    (!!viewerRole && ["admin", "sales_director", "director_of_operations"].includes(viewerRole));
  const viewerEmail = (viewer?.email ?? "").trim().toLowerCase();

  const [roster, setRoster] = useState<Member[]>([]);
  const [layouts, setLayouts] = useState<Record<string, WidgetLayoutRow>>({});
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [selId, setSelId] = useState<string>("");
  const [draft, setDraft] = useState<WidgetKey[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);

  const load = useCallback(async () => {
    setLoadErr(null);
    const [r, l] = await Promise.all([
      supabase
        .from("team_roster")
        .select("id,full_name,email,role,secondary_role")
        .eq("is_active", true)
        .order("full_name"),
      fetchAllWidgetLayouts(),
    ]);
    if (r.error) { setLoadErr(r.error.message); setLoading(false); return; }
    setRoster((r.data ?? []) as Member[]);
    setLayouts(l);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const selected = useMemo(() => roster.find((m) => m.id === selId) ?? null, [roster, selId]);

  // The board as it stands today (saved row → per-person default → role default).
  const savedKeys: WidgetKey[] | null = selId ? (layouts[selId]?.widgets ?? null) : null;
  const liveKeys = useMemo(
    () => (selected ? resolveWidgets(savedKeys, selected.id, selected.role, selected.email) : []),
    [selected, savedKeys],
  );
  const defaultKeys = useMemo(
    () => (selected ? defaultWidgetsFor(selected.id, selected.role, selected.email) : []),
    [selected],
  );
  const source = boardSource(savedKeys, selected?.id, selected?.role);

  // Load the draft whenever the selection (or its saved row) changes.
  useEffect(() => {
    setDraft(liveKeys);
    setSaveErr(null);
    setFlash(null);
  }, [selId, liveKeys.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !sameKeys(draft, liveKeys);

  // Widget pools for this person.
  const eligible = useMemo(
    () => WIDGET_ORDER.filter((k) => isWidgetEligible(k, selected?.role, selected?.email)),
    [selected],
  );
  const available = eligible.filter((k) => !draft.includes(k));
  const blocked = useMemo(
    () =>
      WIDGET_ORDER
        .filter((k) => !isWidgetEligible(k, selected?.role, selected?.email))
        .map((k) => ({ key: k, reason: ineligibleReason(k, selected?.role, selected?.email) })),
    [selected],
  );

  // Preview renders under YOUR permissions, so skip anything you're personally
  // carved out of (Jeremy/financials) rather than rendering its 42501 error.
  const previewSkipped = draft.filter(
    (k) => viewerEmail && WIDGETS[k].excludeEmails?.some((x) => x.toLowerCase() === viewerEmail),
  );
  const previewKeys = draft.filter((k) => !previewSkipped.includes(k));

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= draft.length) return;
    const next = [...draft];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft(next);
  };

  async function onSave() {
    if (!selected) return;
    setSaving(true); setSaveErr(null); setFlash(null);
    const err = await saveWidgetLayout(selected.id, draft, viewer?.id ?? null);
    setSaving(false);
    if (err) { setSaveErr(err); return; }
    setFlash(`Saved — ${selected.full_name.split(" ")[0]}'s board updates on their next load.`);
    await load();
  }

  async function onReset() {
    if (!selected) return;
    if (!window.confirm(`Clear ${selected.full_name}'s custom board and fall back to their ${WIDGET_OVERRIDES_BY_MEMBER[selected.id] ? "per-person" : "role"} default?`)) return;
    setSaving(true); setSaveErr(null); setFlash(null);
    const err = await resetWidgetLayout(selected.id);
    setSaving(false);
    if (err) { setSaveErr(err); return; }
    setFlash("Reset to default.");
    await load();
  }

  // ── render ──────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="max-w-[1180px] mx-auto p-6 text-slate-500">Loading team…</div>;
  }
  if (loadErr) {
    return (
      <div className="max-w-[1180px] mx-auto p-6">
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">
          Couldn't load the roster: {loadErr}{" "}
          <button className="font-bold underline" onClick={() => { setLoading(true); void load(); }}>Retry</button>
        </div>
      </div>
    );
  }

  const grouped = ROLE_ORDER
    .map((r) => ({ role: r, people: roster.filter((m) => m.role === r) }))
    .concat(
      Array.from(new Set(roster.map((m) => m.role).filter((r) => !ROLE_ORDER.includes(r))))
        .sort()
        .map((r) => ({ role: r, people: roster.filter((m) => m.role === r) })),
    )
    .filter((g) => g.people.length > 0);

  const customCount = Object.keys(layouts).length;
  const landing = selected?.role ? LANDING_BY_ROLE[selected.role] : undefined;
  // Oct 27, 2026: canvassers get their own home page (CanvasserHome), which
  // doesn't read a saved board, so none can be saved for them here.
  const fixedHome = selected?.role === "canvasser";

  return (
    <div className="max-w-[1180px] mx-auto px-4 py-5 pb-16 text-slate-900">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-1">
        <div>
          <h1 className="text-[22px] font-extrabold tracking-tight">🎛 Team Boards</h1>
          <p className="text-sm text-slate-500">
            See what each person's home page shows them — and change it for them.
          </p>
        </div>
        <div className="text-[11px] text-slate-500">
          {customCount === 0
            ? "No custom boards yet — everyone is on their role default."
            : `${customCount} custom board${customCount === 1 ? "" : "s"} saved`}
        </div>
      </div>

      {!canEdit && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          You can look, but saving is limited to Greg, Chad, and Jennifer.
        </div>
      )}

      {/* person picker */}
      <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
          Team member
        </label>
        <select
          value={selId}
          onChange={(e) => setSelId(e.target.value)}
          className="w-full sm:w-[420px] border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Pick someone…</option>
          {grouped.map((g) => (
            <optgroup key={g.role} label={roleLabel(g.role)}>
              {g.people.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name}
                  {layouts[m.id] ? " • custom" : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {!selected && (
        <div className="mt-4 text-sm text-slate-500">
          Pick a name above to see their board.
        </div>
      )}

      {selected && (
        <>
          {/* who + where the board comes from */}
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[17px] font-extrabold">{selected.full_name}</div>
                <div className="text-xs text-slate-500">
                  {roleLabel(selected.role)}
                  {selected.secondary_role ? ` · also ${roleLabel(selected.secondary_role)}` : ""}
                  {selected.email ? ` · ${selected.email}` : ""}
                </div>
              </div>
              <span
                title={SOURCE_CHIP[source].hint}
                className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-bold border ${SOURCE_CHIP[source].cls}`}
              >
                {SOURCE_CHIP[source].label}
              </span>
            </div>

            {layouts[selected.id] && (
              <div className="mt-2 text-[11px] text-slate-500">
                Last changed {new Date(layouts[selected.id].updated_at).toLocaleDateString()}
                {layouts[selected.id].updated_by
                  ? ` by ${roster.find((m) => m.id === layouts[selected.id].updated_by)?.full_name ?? "someone"}`
                  : ""}
              </div>
            )}

            {fixedHome && (
              <div className="mt-3 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-800">
                Canvassers have their own home page: assigned areas, Log a lead, My Pay and their numbers against their goals.
                A board saved here isn't shown to them, so saving is off.
              </div>
            )}
            {landing && (
              <div className="mt-3 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-800">
                This role normally opens straight to <strong>{landing}</strong> instead of home.
                {draft.length > 0
                  ? " Because they have a board, they'll land on home instead — the bottom nav still gets them to that board."
                  : " Give them a board and they'll land on home instead."}
              </div>
            )}
          </div>

          {/* editor */}
          <div className="mt-4 grid lg:grid-cols-2 gap-4 items-start">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                Their board · in order
              </div>

              {draft.length === 0 ? (
                <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg px-3 py-6 text-center">
                  No widgets — their home page shows the schedule and My Business only.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {draft.map((k, i) => (
                    <div
                      key={k}
                      className="flex items-center gap-2 border border-slate-200 rounded-lg px-2.5 py-2 bg-slate-50"
                    >
                      <span className="text-base leading-none">{WIDGETS[k].icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{WIDGETS[k].title}</div>
                        <div className="text-[10.5px] text-slate-500 truncate">
                          {k === "today"
                            ? "mobile only — renders at the very top on phones"
                            : `opens ${WIDGETS[k].deepLink}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => move(i, -1)}
                          disabled={!canEdit || i === 0}
                          className="px-1.5 py-0.5 text-slate-500 hover:text-slate-900 disabled:opacity-25"
                          title="Move up"
                        >▲</button>
                        <button
                          onClick={() => move(i, 1)}
                          disabled={!canEdit || i === draft.length - 1}
                          className="px-1.5 py-0.5 text-slate-500 hover:text-slate-900 disabled:opacity-25"
                          title="Move down"
                        >▼</button>
                        <button
                          onClick={() => setDraft(draft.filter((x) => x !== k))}
                          disabled={!canEdit}
                          className="px-1.5 py-0.5 text-slate-400 hover:text-rose-600 disabled:opacity-25"
                          title="Remove"
                        >✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* add */}
              {available.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                    Add a widget
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {available.map((k) => (
                      <button
                        key={k}
                        onClick={() => setDraft([...draft, k])}
                        disabled={!canEdit}
                        className="inline-flex items-center gap-1 border border-slate-300 rounded-full px-2.5 py-1 text-xs font-bold text-slate-700 hover:border-orange-400 hover:text-orange-700 disabled:opacity-40"
                      >
                        <span>{WIDGETS[k].icon}</span>{WIDGETS[k].title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* blocked */}
              {blocked.length > 0 && (
                <details className="mt-4">
                  <summary className="text-[11px] font-bold uppercase tracking-wider text-slate-400 cursor-pointer">
                    Not available to {roleLabel(selected.role)} ({blocked.length})
                  </summary>
                  <div className="mt-2 space-y-1">
                    {blocked.map(({ key, reason }) => (
                      <div key={key} className="flex items-center gap-2 text-[11px] text-slate-400">
                        <span className="opacity-60">{WIDGETS[key].icon}</span>
                        <span className="font-semibold">{WIDGETS[key].title}</span>
                        <span className="text-slate-400">— {reason}</span>
                      </div>
                    ))}
                    <p className="text-[10.5px] text-slate-400 pt-1">
                      These are locked because the board or RPC behind them turns this
                      role away — adding one would show them an error, not data.
                    </p>
                  </div>
                </details>
              )}

              {/* actions */}
              <div className="mt-4 flex items-center gap-2 flex-wrap border-t border-slate-100 pt-3">
                <button
                  onClick={onSave}
                  /* With nothing saved yet, Save is always live — it pins the
                     current default as this person's own board. */
                  disabled={!canEdit || fixedHome || saving || (!dirty && !!savedKeys)}
                  className="bg-orange-600 text-white rounded-lg px-3.5 py-2 text-sm font-bold disabled:opacity-40"
                >
                  {saving ? "Saving…" : "Save board"}
                </button>
                <button
                  onClick={() => setDraft(liveKeys)}
                  disabled={!dirty || saving}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-slate-600 disabled:opacity-40"
                >
                  Undo changes
                </button>
                <button
                  onClick={() => setDraft(defaultKeys)}
                  disabled={!canEdit || saving || sameKeys(draft, defaultKeys)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold text-slate-600 disabled:opacity-40"
                  title="Load their role / per-person default into the editor"
                >
                  Load default
                </button>
                {savedKeys && (
                  <button
                    onClick={onReset}
                    disabled={!canEdit || saving}
                    className="border border-rose-200 text-rose-600 rounded-lg px-3 py-2 text-sm font-bold disabled:opacity-40"
                    title="Delete the custom board so they fall back to the default"
                  >
                    Reset to default
                  </button>
                )}
                {dirty && <span className="text-[11px] text-amber-600 font-bold">unsaved changes</span>}
              </div>

              {saveErr && (
                <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
                  Save failed: {saveErr}
                </div>
              )}
              {flash && (
                <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
                  {flash}
                </div>
              )}
            </div>

            {/* preview */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Preview
                </div>
                <button
                  onClick={() => setShowPreview((v) => !v)}
                  className="text-[11px] font-bold text-slate-500 underline"
                >
                  {showPreview ? "hide" : "show"}
                </button>
              </div>

              {showPreview ? (
                <>
                  <p className="text-[11px] text-slate-500 mb-2">
                    {selected.full_name.split(" ")[0]}'s widgets with their real numbers,
                    rendered under <strong>your</strong> permissions — theirs can differ.
                    {draft.includes("today") && " The 📅 Today hero shows on phones only and isn't previewed here."}
                  </p>
                  {previewSkipped.length > 0 && (
                    <div className="mb-2 rounded-lg bg-slate-100 border border-slate-200 px-3 py-2 text-[11px] text-slate-600">
                      Not previewed for you:{" "}
                      {previewSkipped.map((k) => WIDGETS[k].title).join(", ")} — you're carved
                      out of that board by name, so it would load an error here. It will still
                      render for {selected.full_name.split(" ")[0]}.
                    </div>
                  )}
                  {previewKeys.filter((k) => k !== "today").length === 0 ? (
                    <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg px-3 py-6 text-center">
                      Nothing to preview.
                    </div>
                  ) : (
                    <div className="-mt-4">
                      <WidgetBoard
                        key={`${selected.id}:${previewKeys.join("|")}`}
                        memberId={selected.id}
                        role={selected.role}
                        email={selected.email}
                        scopeIds={[selected.id]}
                        scopeNote="Mine"
                        customKeys={previewKeys}
                        title={null}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-[11px] text-slate-400">Preview hidden.</div>
              )}
            </div>
          </div>

          <p className="mt-4 text-[11px] text-slate-400">
            Boards are per person. Their bottom-nav pins are saved on their own device and
            can't be set from here. Role defaults live in the widget registry —{" "}
            <Link to="/" className="underline">your own board</Link> is the same system.
            {DEFAULT_WIDGETS_BY_ROLE[selected.role]
              ? ` ${roleLabel(selected.role)} default: ${DEFAULT_WIDGETS_BY_ROLE[selected.role].map((k) => WIDGETS[k].title).join(" · ")}.`
              : ` ${roleLabel(selected.role)} has no role default.`}
          </p>
        </>
      )}
    </div>
  );
}
